import { Button } from '@packages/shared/src/ui/Button/Button';
import { StatusChip } from '@packages/shared/src/ui/StatusChip/StatusChip';
import { Dialog } from '@packages/shared/src/ui/Dialog/Dialog';
import { DialogHeader } from '@packages/modules/src/Dialog/DialogHeader/DialogHeader.client';
import { DialogContent } from '@packages/shared/src/ui/Dialog/DialogContent/DialogContent';
import { Notification } from '@packages/shared/src/ui/Notification/Notification';
import { Price } from '@packages/shared/src/ui/Price/Price';
import { useFormatPrice } from '@packages/shared/src/hooks/useFormatPrice/useFormatPrice';
import { getLocalizedPath } from '@packages/shared/src/utils/getLocalizedPath/getLocalizedPath';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { defineMessages, useIntl } from 'react-intl';
import { useEffect, useState } from 'react';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData } from '@packages/gql/generated/shopping';
import { BuyBoxDataFragmentDoc } from '@packages/gql/generated/shopping/BuyBoxDataFragmentDoc';
import { ServiceLayer } from '@packages/modules/src/ServiceLayer/ServiceLayer.client';
import { useRouter } from 'next/navigation';
import { PriceValue } from '@packages/shared/src/ui/Price/PriceValue/PriceValue';
import { StickyInsideParent, StickyOutsideViewport } from '../Sticky/Sticky';
import { HintPanel } from '../HintPanel/HintPanel';
import { PremiumBanner } from '../PremiumBanner/PremiumBanner';
import { BusinessTerms } from '../BusinessTerms/BusinessTerms';
import { Co2CompensationFlag } from '../Co2CompensationFlag/Co2CompensationFlag';
import { checkoutPathByPage } from '../../utils/checkoutPages';

/* GraphQL */ `
  fragment BuyBoxData on Basket {
    subTotal {
      amount
      currency
    }
    saving {
      amount
      currency
    }
    freeShipping
    banners {
      type
      articleNumber
      coupon
    }
    targetPage
  }
`;

const messages = defineMessages({
  buyBoxHintHeader: {
    id: 'BuyBox.HintHeader',
    defaultMessage: 'Tipp',
  },
  subTotal: {
    id: 'BuyBox.SubTotal',
    defaultMessage: 'Zwischensumme',
  },
  priceReduction: {
    id: 'BuyBox.PriceReduction',
    defaultMessage: 'Du sparst bei deinem Einkauf',
  },
  legalHint: {
    id: 'BuyBox.LegalHint',
    defaultMessage: 'Alle Angaben in Euro inkl. MwSt.',
  },
  shippingCostHint: {
    id: 'BuyBox.ShippingCostHint',
    defaultMessage: 'zzgl. Versandkosten',
  },
  couponDiscountHint: {
    id: 'BuyBox.CouponDiscountHint',
    defaultMessage: 'Gutschein- und Rabattcodes werden in Schritt 5 zusätzlich abgezogen!',
  },
  goToCheckout: {
    id: 'BuyBox.GoToCheckout',
    defaultMessage: 'Zur Kasse',
  },
});
export interface BuyBoxProps {
  data?: FragmentType<typeof BuyBoxDataFragmentDoc>;
}

const StickyLandscapeWrapper = ({
  children,
  onViewportChange,
  ...props
}: {
  children: React.ReactNode;
  onViewportChange: (isInView: boolean) => void;
}) => {
  useEffect(() => {
    onViewportChange(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <div {...props}>{children}</div>;
};

export const BuyBox: React.FC<BuyBoxProps> = ({ data }) => {
  const fragmentData = getFragmentData(BuyBoxDataFragmentDoc, data);
  const { locale, formatMessage } = useIntl();
  const router = useRouter();
  const { formatPrice } = useFormatPrice();
  const {
    order: { hasCo2Compensation, premium },
  } = useConfig();
  const [isPortrait, setIsPortrait] = useState(true);
  const [shippingCostModalOpen, setShippingCostModalOpen] = useState<boolean>(false);
  const [hasBonusCodes, setHasBonusCodes] = useState<boolean>(false);
  // track if we're using the relative positioning wrapper
  const [isInViewport, setIsInViewport] = useState(true);

  // detect orientation only
  useEffect(() => {
    const portraitQuery = window.matchMedia('(orientation: portrait)');

    setIsPortrait(portraitQuery.matches);

    const handleOrientationChange = (e: MediaQueryListEvent) => setIsPortrait(e.matches);

    portraitQuery.addEventListener('change', handleOrientationChange);

    return () => {
      portraitQuery.removeEventListener('change', handleOrientationChange);
    };
  }, []);

  const handleViewportChange = (isInView: boolean) => {
    setIsInViewport(isInView);
  };

  useEffect(() => {
    const bonusCodes = [];
    // TODO for api manager
    // const bonusCodes = apiKeyManager.getBonuscodes();
    setHasBonusCodes(bonusCodes.length > 0);
  }, []);

  if (!fragmentData) {
    return null;
  }

  const { banners, subTotal, saving, targetPage } = fragmentData;

  // modify the wrapper for non-portrait mode
  const MobileStickyWrapper = isPortrait ? StickyOutsideViewport : StickyLandscapeWrapper;

  // mobile: show detailed content based on portrait mode and viewport
  const showMobileDetailedContent = !isPortrait || isInViewport;

  const shouldHideCo2HintDesktop = banners && banners.find((banner) => banner.type === 'PREMIUM');

  return (
    <>
      {/* tablet+ view */}
      <StickyInsideParent
        className="bg-color-background-base hidden shadow-xs md:block lg:shadow-none"
        onViewportChange={handleViewportChange}
      >
        <div className="border-color-grey relative box-border rounded-sm border">
          <div className="bg-color-background-base p-1.5 pb-2.5">
            <div className="flex justify-between">
              <div className="text-size-body-base text-color-text-dark font-bold">
                {formatMessage(messages.subTotal)}
              </div>
              {subTotal && (
                <Price size="small" className="text-color-text-dark">
                  <PriceValue>
                    {formatPrice(subTotal.amount, subTotal.currency satisfies string)}
                  </PriceValue>
                </Price>
              )}
            </div>
            {saving && (
              <div className="flex justify-between whitespace-nowrap">
                <div>
                  <span className="text-size-body-sm text-color-success-dark break-word font-bold">
                    {formatMessage(messages.priceReduction)}
                  </span>
                </div>
                <div className="bg-color-success text-size-body-xs text-color-text-dark flex items-center rounded-sm px-1 py-0">
                  {formatPrice(saving.amount, saving.currency satisfies string)}
                </div>
              </div>
            )}
            {/* Desktop always shows detailed content */}
            <div className="text-color-text-darktransparent text-size-body-xs mt-1 flex flex-col">
              <div>{formatMessage(messages.legalHint)}</div>
              <div>
                <Button
                  variant="link"
                  size="small"
                  className="text-color-text-darktransparent"
                  onClick={() => setShippingCostModalOpen(true)}
                  onKeyDown={(e) => e.key === 'Enter' && setShippingCostModalOpen(true)}
                >
                  {formatMessage(messages.shippingCostHint)}
                </Button>
              </div>
            </div>
            {(saving || hasBonusCodes) && (
              <Notification severity="info" variant="outline" className="text-size-body-sm mt-2">
                {formatMessage(messages.couponDiscountHint)}
              </Notification>
            )}
            <div className="mt-1.5">
              <Button
                className="w-full"
                color="primary"
                onClick={() =>
                  router.push(getLocalizedPath(checkoutPathByPage[targetPage], locale))
                }
              >
                {formatMessage(messages.goToCheckout)}
              </Button>
            </div>
            {/* CO2 hint - show unless hidden by premium banner */}
            {hasCo2Compensation && !shouldHideCo2HintDesktop && (
              <div className="mt-1.5">
                <Co2CompensationFlag />
              </div>
            )}

            {/* Premium banners - desktop only */}
            {banners && banners.length > 0 && (
              <div className="mt-3">
                {banners.map((banner) => {
                  if (banner.type === 'PREMIUM' && premium.enabled) {
                    return (
                      <HintPanel
                        key={banner.articleNumber}
                        header={
                          <StatusChip
                            className="text-color-success-contrastText uppercase"
                            color="success"
                          >
                            {formatMessage(messages.buyBoxHintHeader)}
                          </StatusChip>
                        }
                      >
                        <PremiumBanner
                          articleNumber={banner.articleNumber || undefined}
                          mode="Basket"
                        />
                      </HintPanel>
                    );
                  }
                  return null;
                })}
              </div>
            )}
          </div>
          <BusinessTerms />
        </div>
      </StickyInsideParent>

      {/* mobile view */}
      <MobileStickyWrapper
        className="bg-color-background-base shadow-xs md:hidden"
        onViewportChange={handleViewportChange}
      >
        <div className="border-color-grey relative box-border rounded-none border-0 border-solid">
          <div className="bg-color-background-base p-1.5 pb-2.5">
            <div className="flex justify-between">
              <div className="text-size-body-base text-color-text-dark font-bold">
                {formatMessage(messages.subTotal)}
              </div>
              {subTotal && (
                <Price size="small" className="text-color-text-dark">
                  <PriceValue>
                    {formatPrice(subTotal.amount, subTotal.currency satisfies string)}
                  </PriceValue>
                </Price>
              )}
            </div>
            {saving && (
              <div className="flex justify-between whitespace-nowrap">
                <div>
                  <span className="text-size-body-sm text-color-success-dark break-word font-bold">
                    {formatMessage(messages.priceReduction)}
                  </span>
                </div>
                <div className="bg-color-success text-size-body-xs text-color-text-dark flex items-center rounded-sm px-1 py-0">
                  {formatPrice(saving.amount, saving.currency satisfies string)}
                </div>
              </div>
            )}
            {/* mobile: conditionally show detailed content based on viewport/orientation */}
            {showMobileDetailedContent && (
              <>
                <div className="text-color-text-darktransparent text-size-body-xs mt-1 flex flex-col">
                  <div>{formatMessage(messages.legalHint)}</div>
                  <div>
                    <Button
                      variant="link"
                      size="small"
                      className="text-color-text-darktransparent"
                      onClick={() => setShippingCostModalOpen(true)}
                      onKeyDown={(e) => e.key === 'Enter' && setShippingCostModalOpen(true)}
                    >
                      {formatMessage(messages.shippingCostHint)}
                    </Button>
                  </div>
                </div>
                {(saving || hasBonusCodes) && (
                  <Notification
                    severity="info"
                    variant="outline"
                    className="text-size-body-sm mt-2"
                  >
                    {formatMessage(messages.couponDiscountHint)}
                  </Notification>
                )}
              </>
            )}
            <div className="mt-1.5">
              <Button
                className="w-full"
                color="primary"
                onClick={() =>
                  router.push(getLocalizedPath(checkoutPathByPage[targetPage], locale))
                }
              >
                {formatMessage(messages.goToCheckout)}
              </Button>
            </div>
          </div>
          {showMobileDetailedContent && <BusinessTerms />}
        </div>
      </MobileStickyWrapper>

      {/* shared shipping cost modal */}
      <Dialog
        title=""
        isOpen={shippingCostModalOpen}
        onClose={() => setShippingCostModalOpen(false)}
      >
        <DialogHeader onClose={() => setShippingCostModalOpen(false)} />
        <DialogContent>
          <div className="px-2">
            <ServiceLayer serviceLayerId="versandkosten_io" />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
